// FAO Stewart Smith
// NOTE/FYI - This is a copy of the Images Helper from the DFI initiative. This file will need to moved to a common location
// when DFI merged in. This version has been updated to provide improved object property handling for items with no images
// so use this version. When re-located, update the relative path to the AmplifyConfig in imports below.

import { API } from 'aws-amplify'
import { get as localDbGet, set as localDbSet } from 'idb-keyval'
import md5 from 'md5'
import AmplifyConfig from '../../../aws-exports'
import { getDataURIFromBlob } from "helpers/generalHelpers"
import { makeImageLocalKey } from 'helpers/imageHelpers'
import { deviceImages } from 'helpers/deviceImages'
import { MISSING_S3_IMAGE } from 'constants'

function updateDataUriAsMissingImage(images = []) {
  const newImages = []

  for (const image of images) {
    const newImage = { ...image }

    if (!newImage?.dataUri) {
      newImage.dataUri = MISSING_S3_IMAGE
    }

    newImages.push(newImage)
  }

  return newImages
}

export async function getImageUrisFromIndexDb({ item = {}, facilityId }) {
  const { images = [] } = item

  let newImages = []

  for (const image of images) {
    const newImage = { ...image }

    let dataUri

    // Image has been uploaded to S3 (it contains a picture property with location information)
    if (image?.picture) {
      dataUri = await localDbGet(
        makeImageLocalKey({
          hash: md5(image.picture?.key),
          facilityId
        })
      )
    }

    // if image not uploaded to S3
    if (!dataUri) {
      dataUri = await localDbGet(
        makeImageLocalKey({
          hash: image.hash,
          facilityId,
        })
      )
    }

    // if image not uploaded to S3 & stored locally as pending
    if (!dataUri) {
      dataUri = await localDbGet(
        makeImageLocalKey({
          type: 'pending',
          hash: image.hash,
          facilityId,
        })
      )
    }

    newImage.dataUri = dataUri

    newImages.push(newImage)
  }

  return newImages
}

export async function getImagesToDrawerState({
  facilityId,
  forceUpdate,
  imagesDownloadEnabled,
  isOnline,
  item,
  mounted,
  setAreImagesLoaded,
  setImages,
  setImagesForOnlineDownload
}) {
  const images = await getImageUrisFromIndexDb({ item, facilityId }) || []
  const allImagesLinked = images.length ? images.every(i => i.dataUri) : true
  
  if (!mounted) {
    return
  }

  if (allImagesLinked) {
    setImages(images)
    setAreImagesLoaded(true)
  } else {
    // handle missing localDB image dataUri's
    if ((imagesDownloadEnabled && isOnline) || (imagesDownloadEnabled && !isOnline)) {
      // Images may be missing either to loss in connection or potentially missing S3 image(s) buckets
      // Update missing dataUri's with MISSING_S3_IMAGE key to show placeholder image
      const managedImages = updateDataUriAsMissingImage(images)

      setImages(managedImages)
      setAreImagesLoaded(true)
    }

    if (!imagesDownloadEnabled && isOnline) {
      // The image(s) will be downloaded by the downloadOnlineImages function and then added to localDb from the image component
      setImagesForOnlineDownload(images)
      
      return
    }
  }
    
  forceUpdate()
}

async function setImageToLocalStorage(image, facilityId, tenantId) {
  const imageKeyPending = makeImageLocalKey({
    type: 'pending',
    hash: image.hash,
    facilityId
  })

  const existingPendingImage = await localDbGet(imageKeyPending)

  if (existingPendingImage) {
    return
  }

  // add to download to ensure download not triggered
  await deviceImages.addDownloaded({
    facilityId,
    localKey: makeImageLocalKey({
      hash: md5(image.picture.key),
      facilityId
    })
  })

  // Set as a pending image to upload
  await localDbSet(imageKeyPending, image.dataUri)

  // Set in mutation v2 queue to upload image
  let fileName = image.picture.key.split('/')[1]

  await localDbSet(
    `mutationv2-${fileName}`,
    JSON.stringify({
      facilityId,
      hash: image.hash,
      key: image.picture.key,
      tenantId
    })
  )
}

export async function downloadOnlineImages({
  facilityId,
  images = [],
  setImages = () => {}
}) {
  try {
    for (const image of images) {
      const { picture } = image

      const {data: imageResponse} = await API.post("rest", "/items/get-image", {
        body: picture,
        response: true,
      })

      const blob = new Blob([new Uint8Array(imageResponse.Body.data)], {
        type: `${imageResponse.ContentType}+${imageResponse.ContentEncoding}`
      })

      const imageDataUri = await getDataURIFromBlob(blob)

      localDbSet(
        makeImageLocalKey({
          hash: md5(picture?.key),
          facilityId
        }),
        imageDataUri
      )
    }

    // Now we need to load the new images into the drawerImages to display in panel
    const newImages = await getImageUrisFromIndexDb({ item: {images: [ ...images ]}, facilityId })

    setImages(newImages)
  } catch (error) {
    console.error('unable to retrieve images from S3 and update localDB image store')
  }
}
